import React, { useState } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import {
  Box,
  Typography,
  Paper,
  Fade,
  Grid,
  Stack,
  Avatar,
  Card,
  CardContent,
  Divider,
  Toolbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Alert,
  Collapse,
  IconButton,
} from '@mui/material';
import {
  Business,
  CalendarToday,
  Receipt,
  Schedule,
  AttachMoney,
  Warning as WarningIcon,
  CheckCircle,
  Cancel,
  Description,
  ExpandMore,
  ExpandLess,
  Download,
} from '@mui/icons-material';
import HomeHeader from '../../../../Components/HomeHeader';
import HomeFooter from '../../../../Components/HomeFooter';
import { professionalTheme } from './themes';
import ApprovalStamp from './approvalstamp';
import InvoiceActions from './invoiceactions';
import BankingInfo from './bankinginfo';
import PaymentCTA from './paymentCTA';

const RegularInvoiceDesign = ({ 
  invoice, 
  user, 
  company,
  companyInfo,
  invoiceSettings,
  payouts, 
  onDownloadPDF,
  onCopyInvoiceNumber,
  pdfGenerating, 
  formatDate, 
  getStatusColor, 
  getStatusText,
  getEffectiveValue,
  getInvoiceNotes,
  getInvoiceTerms,
  daysLate,
  totalDaysToPay,
  calculatedDueDate,
  supportingDocument,
  needsApproval,
  userInfo,
  onOpenApprovalDialog,
  onOpenLoginDialog,
  billedToEmail,
  billedToName,
  isAuthenticated = false, // NEW PROP
}) => {
  const [showSupportingDoc, setShowSupportingDoc] = useState(false);
  
  const safePayouts = Array.isArray(payouts) ? payouts.filter(p => p != null) : [];
  
  const safeAmount = (amt) => (typeof amt === 'number' ? amt : 0);
  const subtotal = safePayouts.reduce((acc, p) => acc + safeAmount(p?.amount), 0);
  const amountDue = safeAmount(invoice?.amount_due);
  const outstanding = amountDue;

  const effectiveInvoiceNumber = getEffectiveValue('invoice_number', invoice?.po_number);
  const effectiveDateIssued = getEffectiveValue('date_of_issue', invoice?.created_at);
  const effectiveBilledTo = getEffectiveValue('billed_to', invoice?.client_name || (user ? `${user.first_name || ''} ${user.last_name || ''}` : ''));
  
  const paymentDate = invoice?.payment_date;
  const paymentTerm = invoice?.payment_term;
  const invoiceStatus = invoice?.status || invoice?.invoice_status;
  
  const invoiceNotes = getInvoiceNotes();
  const invoiceTerms = getInvoiceTerms();

  return (
    <ThemeProvider theme={professionalTheme}>
      <Box sx={{ 
        minHeight: '100vh', 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex', 
        flexDirection: 'column' 
      }}>
        <HomeHeader />
        <Toolbar />

        {/* Action Bar - Pass isAuthenticated prop */}
        <InvoiceActions
          onDownloadPDF={onDownloadPDF}
          pdfGenerating={pdfGenerating}
          onCopyInvoiceNumber={onCopyInvoiceNumber}
          invoiceNumber={effectiveInvoiceNumber}
          invoiceStatus={invoiceStatus}
          createdDate={effectiveDateIssued}
          getStatusText={getStatusText}
          getStatusColor={getStatusColor}
          formatDate={formatDate}
          variant="regular"
          isAuthenticated={isAuthenticated}
        />

        <Box sx={{ py: 2, flex: 1, maxWidth: '100vw', overflowX: 'hidden' }}>
          {/* Enhanced Approval Banner with Approver Details */}
          {needsApproval && (
            <Fade in={true} timeout={600}>
              <Box sx={{ width: { xs: '95%', sm: '210mm' }, maxWidth: '100%', m: '0 auto', mb: 2 }}>
                <Alert 
                  severity="warning" 
                  sx={{ 
                    borderRadius: 3,
                    '& .MuiAlert-message': { width: '100%' }
                  }}
                >
                  <Stack spacing={2}>
                    <Typography variant="h6" fontWeight={600}>
                      This invoice requires approval
                    </Typography>
                    
                    <Typography variant="body2">
                      <strong>Action Required:</strong> This invoice needs to be reviewed and approved by the billed party:
                    </Typography>
                    
                    {/* Highlighted Approver Details Box */}
                    <Box sx={{ 
                      pl: 2, 
                      py: 1.5, 
                      borderLeft: '3px solid',
                      borderColor: 'warning.main',
                      bgcolor: 'rgba(237, 108, 2, 0.08)',
                      borderRadius: 1
                    }}>
                      <Typography variant="body2" fontWeight={600} gutterBottom>
                        Approver Details:
                      </Typography>
                      <Typography variant="body2">
                        Name: <strong>{billedToName || 'Not specified'}</strong>
                      </Typography>
                      <Typography variant="body2">
                        Email: <strong>{billedToEmail || 'Not specified'}</strong>
                      </Typography>
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      If you are {billedToName || 'the billed party'}, please review the invoice details below and approve or decline.
                    </Typography>
                    
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      {userInfo ? (
                        <>
                          <Button
                            variant="contained"
                            color="success"
                            startIcon={<CheckCircle />}
                            onClick={onOpenApprovalDialog}
                            sx={{ borderRadius: 2 }}
                          >
                            Approve Invoice
                          </Button>
                          <Button
                            variant="outlined"
                            color="error"
                            startIcon={<Cancel />}
                            onClick={onOpenApprovalDialog}
                            sx={{ borderRadius: 2 }}
                          >
                            Decline Invoice
                          </Button>
                        </>
                      ) : (
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={onOpenLoginDialog}
                          sx={{ borderRadius: 2 }}
                        >
                          Login to Approve/Decline
                        </Button>
                      )}
                    </Stack>
                  </Stack>
                </Alert>
              </Box>
            </Fade>
          )}

          <Paper
            id="invoice-content"
            sx={{
              p: { xs: 3, sm: 5 },
              width: { xs: '95%', sm: '210mm' },
              maxWidth: '100%',
              minHeight: { xs: 'auto', sm: '297mm' },
              m: '0 auto',
              background: 'white',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 8,
                background: 'linear-gradient(90deg, #3b82f6, #1e293b, #10b981)',
              }
            }}
          >
            {/* Header Section */}
            <Box sx={{ mt: 2, mb: 5 }}>
              <Grid container spacing={3} alignItems="flex-start">
                <Grid item xs={12} md={6}>
                  <Stack spacing={2.5}>
                    <Box>
                      {companyInfo.logo ? (
                        <img 
                          src={companyInfo.logo} 
                          alt={`${companyInfo.name} Logo`} 
                          style={{ maxWidth: 200, maxHeight: 80, marginBottom: 16 }} 
                        />
                      ) : (
                        <Avatar 
                          sx={{ 
                            width: 60, 
                            height: 60, 
                            background: 'linear-gradient(45deg, #3b82f6, #1e293b)',
                            fontSize: '1.5rem',
                            fontWeight: 'bold',
                            mb: 2
                          }}
                        >
                          {companyInfo.name.charAt(0)}
                        </Avatar>
                      )}
                      <Typography variant="h4" sx={{ fontWeight: 800, color: '#1e293b', mb: 1 }}>
                        INVOICE
                      </Typography>
                      <Typography variant="h6" sx={{ color: '#64748b', fontWeight: 400 }}>
                        From {companyInfo.name}
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Card sx={{ background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)', borderRadius: 3 }}>
                    <CardContent sx={{ p: 2.5 }}>
                      <Stack spacing={2}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="textSecondary">Total Amount Due</Typography>
                          <Chip 
                            label={getStatusText(invoiceStatus)} 
                            color={getStatusColor(invoiceStatus)}
                            size="small"
                          />
                        </Box>
                        <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#1e293b' }}>
                          ${outstanding.toFixed(2)}
                        </Typography>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>

            {/* Approval Stamp */}
            {invoice?.approval_proof && (
              <ApprovalStamp 
                approvalInfo={invoice.approval_proof}
                invoice={invoice}
              />
            )}

            {/* Embedded Supporting Document Section */}
            {supportingDocument && (
              <Card 
                variant="outlined" 
                data-supporting-doc-section
                sx={{ 
                  mb: 5, 
                  borderRadius: 3,
                  border: '2px solid',
                  borderColor: 'primary.main'
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Stack 
                    direction="row" 
                    justifyContent="space-between" 
                    alignItems="center"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setShowSupportingDoc(!showSupportingDoc)}
                  >
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        <Description />
                      </Avatar>
                      <Box>
                        <Typography variant="h6" fontWeight={600}>
                          Supporting Documents Attached
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Click to {showSupportingDoc ? 'hide' : 'view'} attached documents
                        </Typography>
                      </Box>
                    </Stack>
                    <IconButton>
                      {showSupportingDoc ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  </Stack>
                  
                  <Collapse in={showSupportingDoc}>
                    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                      <iframe
                        src={supportingDocument}
                        style={{ 
                          width: '100%', 
                          height: '500px', 
                          border: '1px solid #e0e0e0',
                          borderRadius: '8px'
                        }}
                        title="Supporting Document PDF"
                      />
                      <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                        <Button
                          variant="outlined"
                          startIcon={<Download />}
                          href={supportingDocument}
                          download
                          target="_blank"
                        >
                          Download Document
                        </Button>
                      </Stack>
                    </Box>
                  </Collapse>
                </CardContent>
              </Card>
            )}

            {/* Invoice Details */}
            <Grid container spacing={3} sx={{ mb: 5 }}>
              <Grid item xs={12} md={3}>
                <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', height: '100%' }}>
                  <CardContent sx={{ p: 2.5, textAlign: 'center' }}>
                    <Avatar sx={{ background: '#f1f5f9', color: '#64748b', mx: 'auto', mb: 1.5 }}>
                      <CalendarToday />
                    </Avatar>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                      Issue Date
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {formatDate(effectiveDateIssued)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', height: '100%' }}>
                  <CardContent sx={{ p: 2.5, textAlign: 'center' }}>
                    <Avatar sx={{ background: '#f1f5f9', color: '#64748b', mx: 'auto', mb: 1.5 }}>
                      <Receipt />
                    </Avatar>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                      Invoice #
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {effectiveInvoiceNumber || 'N/A'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', height: '100%' }}>
                  <CardContent sx={{ p: 2.5, textAlign: 'center' }}>
                    <Avatar sx={{ background: '#f1f5f9', color: '#64748b', mx: 'auto', mb: 1.5 }}>
                      <Schedule />
                    </Avatar>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                      Payment Term
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {paymentTerm ? `${paymentTerm} days` : 'N/A'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', height: '100%' }}>
                  <CardContent sx={{ p: 2.5, textAlign: 'center' }}>
                    <Avatar sx={{ background: '#f1f5f9', color: '#64748b', mx: 'auto', mb: 1.5 }}>
                      <AttachMoney />
                    </Avatar>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                      {paymentDate ? 'Payment Date' : 'Due Date'}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {paymentDate ? formatDate(paymentDate) : (calculatedDueDate ? formatDate(calculatedDueDate) : formatDate(invoice?.created_at))}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Payment Timeline Info */}
            {(totalDaysToPay !== 'N/A' || (daysLate !== 'N/A' && daysLate > 0)) && (
              <Card sx={{ mb: 5, borderRadius: 3, bgcolor: 'info.50', border: '1px solid', borderColor: 'info.200' }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Typography variant="h6" fontWeight={600} gutterBottom sx={{ color: 'info.main' }}>
                    Payment Timeline
                  </Typography>
                  <Grid container spacing={2.5}>
                    {totalDaysToPay !== 'N/A' && (
                      <Grid item xs={12} sm={6}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <CalendarToday sx={{ color: 'info.main' }} />
                          <Typography variant="body2">Total Days to Pay:</Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {totalDaysToPay} days
                          </Typography>
                        </Stack>
                      </Grid>
                    )}
                    {daysLate !== 'N/A' && daysLate > 0 && (
                      <Grid item xs={12} sm={6}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <WarningIcon sx={{ color: 'error.main' }} />
                          <Typography variant="body2" color="error">Days Late:</Typography>
                          <Typography variant="body2" fontWeight={600} color="error">
                            {daysLate} days
                          </Typography>
                        </Stack>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            )}

            {/* Billing Information */}
            <Grid container spacing={3} sx={{ mb: 5 }}>
              <Grid item xs={12} md={6}>
                <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0' }}>
                  <CardContent sx={{ p: 2.5 }}>
                    <Stack spacing={2}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ background: '#3b82f6' }}>
                          <Business />
                        </Avatar>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Billed To
                        </Typography>
                      </Box>
                      <Box sx={{ pl: 6 }}>
                        <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                          {effectiveBilledTo || 'N/A'}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {invoice?.client_email || user?.email || 'N/A'}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Items Table */}
            <Card sx={{ mb: 5, borderRadius: 3, overflow: 'hidden' }}>
              <TableContainer>
                <Table>
                  <TableHead sx={{ background: 'linear-gradient(45deg, #1e293b 30%, #334155 90%)' }}>
                    <TableRow>
                      <TableCell sx={{ color: 'white', fontWeight: 600, py: 2 }}>Item</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600, py: 2 }}>Description</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600, py: 2 }}>Destination</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600, py: 2, textAlign: 'right' }}>Rate</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600, py: 2, textAlign: 'center' }}>Qty</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600, py: 2, textAlign: 'right' }}>Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {safePayouts.map((p, index) => (
                      <TableRow 
                        key={p?.id || index} 
                        sx={{ 
                          '&:nth-of-type(odd)': { backgroundColor: '#f8fafc' },
                          '&:last-child td': { borderBottom: 0 } 
                        }}
                      >
                        <TableCell sx={{ py: 2 }}>
                          <Chip label={p?.creator_id || 'N/A'} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell sx={{ py: 2 }}>
                          {p?.payout_notes ? p.notes : `PO Number ${p?.po_number || 'N/A'}`}
                        </TableCell>
                        <TableCell sx={{ py: 2 }}>
                          {p?.destination ? (
                            <Chip label={p.destination} size="small" color="secondary" variant="outlined" />
                          ) : '—'}
                        </TableCell>
                        <TableCell sx={{ py: 2, textAlign: 'right', fontWeight: 500 }}>
                          ${safeAmount(p?.amount).toFixed(2)}
                        </TableCell>
                        <TableCell sx={{ py: 2, textAlign: 'center' }}>1</TableCell>
                        <TableCell sx={{ py: 2, textAlign: 'right', fontWeight: 600 }}>
                          ${safeAmount(p?.amount).toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}

                    {invoice?.invoice_items?.length > 0 && invoice.invoice_items.map((it, idx) => (
                      <TableRow key={idx} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#f8fafc' } }}>
                        <TableCell>Payment to {companyInfo.name}</TableCell>
                        <TableCell sx={{ whiteSpace: 'pre-wrap' }}>{it?.description || 'N/A'}</TableCell>
                        <TableCell>—</TableCell>
                        <TableCell sx={{ textAlign: 'right', fontWeight: 500 }}>${(it?.amount || 0).toFixed(2)}</TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>1</TableCell>
                        <TableCell sx={{ textAlign: 'right', fontWeight: 600 }}>${(it?.amount || 0).toFixed(2)}</TableCell>
                      </TableRow>
                    ))}

                    {safePayouts.length === 0 && (!invoice?.invoice_items || invoice.invoice_items.length === 0) && (
                      <TableRow>
                        <TableCell>Payment to {companyInfo.name}</TableCell>
                        <TableCell>Professional Services</TableCell>
                        <TableCell>—</TableCell>
                        <TableCell sx={{ textAlign: 'right', fontWeight: 500 }}>${amountDue.toFixed(2)}</TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>1</TableCell>
                        <TableCell sx={{ textAlign: 'right', fontWeight: 600 }}>${amountDue.toFixed(2)}</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>

            {/* Summary */}
            <Grid container justifyContent="flex-end" sx={{ mb: 5 }}>
              <Grid item xs={12} sm={6} md={4}>
                <Card sx={{ borderRadius: 3, border: '2px solid #e2e8f0' }}>
                  <CardContent sx={{ p: 2.5 }}>
                    <Stack spacing={2}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body1">Subtotal</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          ${(subtotal || amountDue).toFixed(2)}
                        </Typography>
                      </Stack>
                      <Divider />
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>Total Due</Typography>
                        <Typography 
                          variant="body1" 
                          sx={{ fontWeight: 700, fontSize: '1.1rem' }}
                        >
                          ${outstanding.toFixed(2)}
                        </Typography>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Banking Information */}
            <BankingInfo companyInfo={companyInfo} variant="regular" />

            {/* Payment Terms */}
            {invoiceTerms && (
              <Box sx={{ mb: 5 }}>
                <Card variant="outlined" sx={{ borderRadius: 2, bgcolor: 'grey.50' }}>
                  <CardContent sx={{ p: 2.5 }}>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      Payment Terms & Conditions
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {invoiceTerms}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            )}

            {/* Payment CTA */}
            <PaymentCTA 
              invoice={invoice} 
              invoiceAmount={outstanding} 
              variant="regular" 
            />

            {/* Footer */}
            <Box sx={{ mt: 5, pt: 3, borderTop: '2px solid #e2e8f0', textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                Questions about this invoice? Contact us at {companyInfo.email || 'win@useblitz.co'}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {companyInfo.name}
              </Typography>
              {companyInfo.address_line_1 && (
                <Typography variant="caption" color="text.secondary" display="block">
                  {companyInfo.address_line_1}
                  {companyInfo.city && `, ${companyInfo.city}`}
                  {companyInfo.state && `, ${companyInfo.state}`}
                  {companyInfo.postal_code && ` ${companyInfo.postal_code}`}
                </Typography>
              )}
              {companyInfo.phone_number && (
                <Typography variant="caption" color="text.secondary" display="block">
                  Phone: {companyInfo.phone_number}
                </Typography>
              )}
              {companyInfo.website && (
                <Typography variant="caption" color="text.secondary" display="block">
                  {companyInfo.website}
                </Typography>
              )}
            </Box>
          </Paper>
        </Box>

        <HomeFooter />
      </Box>
    </ThemeProvider>
  );
};

export default RegularInvoiceDesign;